import type { FC, ReactNode } from 'react';
import { useEffect, useReducer } from 'react';
import type { Message } from './Message';
import { MessageLevel } from './MessageLevel';
import type { Messages } from './Messages';
import { MessagesContext } from './MessagesContext';

interface Props {
  readonly children: ReactNode;
  readonly messages?: readonly Message[];
}

interface MessageImpl extends Message {
  readonly id: number;
  readonly expiration: number | undefined;
  readonly onDismiss: { invoke: () => void } | undefined;
}

interface State {
  readonly nextId: number;
  readonly messages: readonly MessageImpl[];
}

interface AddMessageEvent {
  readonly type: 'Add';
  readonly level: MessageLevel;
  readonly text: string;
  readonly timeoutSeconds: number | undefined;
  readonly onDismiss: (() => void) | undefined;
}

interface RemoveMessageEvent {
  readonly type: 'Remove';
  readonly id: number;
}

type Event = AddMessageEvent | RemoveMessageEvent;

const addMessage = (state: State, event: AddMessageEvent): State => {
  const { nextId, messages } = state;
  const newMessage: MessageImpl = {
    id: nextId,
    text: event.text,
    level: event.level,
    expiration: event.timeoutSeconds ? new Date().getTime() + event.timeoutSeconds * 1000 : undefined,
    onDismiss: event.onDismiss ? { invoke: event.onDismiss } : undefined,
  };
  return {
    nextId: nextId + 1,
    messages: [...messages, newMessage],
  };
};

const updateState = (state: State, event: Event): State => {
  switch (event.type) {
    case 'Add':
      return addMessage(state, event);
    case 'Remove':
      state.messages.find((m) => m.id === event.id)?.onDismiss?.invoke();
      return {
        ...state,
        messages: state.messages.filter((m) => m.id !== event.id),
      };
  }
};

export const MessagesProvider: FC<Props> = ({ children, messages }) => {
  const [state, dispatch] = useReducer(
    updateState,
    messages
      ? {
          messages: messages.map((m, id) => ({
            ...m,
            id: id + 1,
            expiration: undefined,
            onDismiss: undefined,
          })),
          nextId: messages.length + 1,
        }
      : { messages: [], nextId: 1 },
  );
  const dismiss = (id: number) => {
    dispatch({ type: 'Remove', id });
  };
  useEffect(() => {
    type ExpiringMessage = MessageImpl & { readonly expiration: number };
    const nextExpiring = state.messages.reduce<ExpiringMessage | undefined>((next, m) => {
      if (m.expiration) {
        if (next) {
          if (m.expiration < next.expiration) {
            return m as ExpiringMessage;
          } else {
            return next;
          }
        } else {
          return m as ExpiringMessage;
        }
      } else {
        return next;
      }
    }, undefined);
    if (nextExpiring) {
      const now = new Date().getTime();
      const delay = nextExpiring.expiration - now;
      const timeoutID = setTimeout(
        () => {
          dismiss(nextExpiring.id);
        },
        delay < 0 ? 0 : delay,
      );
      return () => {
        clearTimeout(timeoutID);
      };
    } else {
      return undefined;
    }
  }, [state.messages]);
  const value: Messages = {
    messages: state.messages,
    showSuccess: (text, timeoutSeconds, onDismiss) => {
      dispatch({
        type: 'Add',
        level: MessageLevel.Success,
        text,
        timeoutSeconds,
        onDismiss,
      });
      return state.nextId;
    },
    showInfo: (text, timeoutSeconds, onDismiss) => {
      dispatch({
        type: 'Add',
        level: MessageLevel.Info,
        text,
        timeoutSeconds,
        onDismiss,
      });
      return state.nextId;
    },
    showWarning: (text, timeoutSeconds, onDismiss) => {
      dispatch({
        type: 'Add',
        level: MessageLevel.Warning,
        text,
        timeoutSeconds,
        onDismiss,
      });
      return state.nextId;
    },
    showError: (text, timeoutSeconds, onDismiss) => {
      dispatch({
        type: 'Add',
        level: MessageLevel.Danger,
        text,
        timeoutSeconds,
        onDismiss,
      });
      return state.nextId;
    },
    dismiss,
  };

  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
};
MessagesProvider.displayName = 'MessagesProvider';
